// hooks/useAccessToken.js
import { useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { API_SCOPE, SILENT_REDIRECT_URI } from '../authConfig';
import { recoverWithInteractiveLogin } from '../utils/authInteractiveFallback';

/**
 * Centralized hook for acquiring access tokens with consistent error handling
 * 
 * Features:
 * - Automatic silent token refresh
 * - Popup fallback for expired refresh tokens
 * - Stable function reference (safe for useEffect dependencies)
 * - Consistent error handling across the app
 * 
 * @returns {Object} { getToken: async function }
 */
export function useAccessToken() {
  const { instance, accounts } = useMsal();

  const getToken = useCallback(async () => {
    // Validate we have an account
    if (!accounts || accounts.length === 0) {
      throw new Error('No authenticated account found. Please login.');
    }

    const tokenRequest = {
      scopes: [API_SCOPE],
      account: accounts[0],
      redirectUri: SILENT_REDIRECT_URI
    };

    try {
      // Try silent token acquisition first (checks cache, auto-refreshes if needed)
      const response = await instance.acquireTokenSilent(tokenRequest);
      return response.accessToken;
    } catch (silentError) {
      console.warn('[useAccessToken] Silent token acquisition failed:', silentError.message);

      const recovered = await recoverWithInteractiveLogin({
        instance,
        error: silentError,
        account: tokenRequest.account,
        reason: 'use_access_token_silent_failure'
      });

      if (recovered) {
        throw new Error('Re-authentication required. Redirecting to sign in...');
      }

      throw new Error(`Authentication failed: ${silentError.message}`);
    }
  }, [instance, accounts]);

  return { getToken };
}
